'use client';

import { FEATURE_AUTH_ENABLED, FEATURE_TESTIMONIOS_ENABLED } from '@/lib/features';
import { AppImage } from '@impacta/shared';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import ContactForm from '@/components/forms/ContactForm';

// ─── Types ───────────────────────────────────────────────────────────────────
interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

interface Testimonial {
  quote: string;
  author: string;
  role: string;
  avatar: string;
  alt: string;
}

// ─── Data ────────────────────────────────────────────────────────────────────
const testimonials: Testimonial[] = [
  {
    quote:
      'Tuve un problema con mi reserva y el equipo de soporte lo resolvió en menos de una hora. Increíble atención al cliente.',
    author: 'Catalina Ramos',
    role: 'Participante de taller de pintura',
    avatar: '/assets/images/ayuda/testimonial-catalina.png',
    alt: 'Catalina Ramos, participante satisfecha con el soporte de IMPACTA.red',
  },
  {
    quote:
      'Nunca había usado una plataforma tan intuitiva. Las respuestas en el centro de ayuda son claras y resuelven todas las dudas.',
    author: 'Andrés Molina',
    role: 'Estudiante de fotografía',
    avatar: '/assets/images/ayuda/testimonial-andres.png',
    alt: 'Andrés Molina, estudiante satisfecho con la plataforma IMPACTA.red',
  },
  {
    quote:
      'Tuve que cancelar por una emergencia y el proceso fue muy sencillo. Me devolvieron el dinero sin complicaciones.',
    author: 'Valentina Torres',
    role: 'Cliente frecuente',
    avatar: '/assets/images/ayuda/testimonial-valentina.png',
    alt: 'Valentina Torres, cliente frecuente de IMPACTA.red',
  },
  {
    quote:
      'El formulario de contacto es muy fácil de usar y respondieron mi consulta en el mismo día. Muy profesionales.',
    author: 'Felipe Contreras',
    role: 'Participante de taller de cocina',
    avatar: '/assets/images/ayuda/testimonial-felipe.png',
    alt: 'Felipe Contreras, participante satisfecho con el soporte de IMPACTA.red',
  },
];

// ─── Hooks ───────────────────────────────────────────────────────────────────
function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

// ─── Component ───────────────────────────────────────────────────────────────
export default function AyudaClient({ initialFaqs }: { initialFaqs?: FAQItem[] | null }) {
  const displayFaqs = initialFaqs || [];
  const [openFAQ, setOpenFAQ] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState('Todos');
  const [testimonialIndex, setTestimonialIndex] = useState(0);

  const heroSection = useInView(0.1);
  const faqSection = useInView(0.1);
  const contactSection = useInView(0.1);
  const testimonialsSection = useInView(0.1);
  const ctaSection = useInView(0.1);

  const visibleFaqs = FEATURE_AUTH_ENABLED
    ? displayFaqs
    : displayFaqs.filter((f) => f.category !== 'Mi Cuenta');

  const categories = ['Todos', ...Array.from(new Set(visibleFaqs.map((f) => f.category)))];

  useEffect(() => {
    const hash = window.location.hash;
    if (hash.startsWith('#faq--')) {
      const slug = hash.replace('#faq--', '');
      const categoryFromHash = categories.find(
        (cat) => cat.toLowerCase().replace(/\s+/g, '-') === slug
      );
      if (categoryFromHash) {
        setActiveCategory(categoryFromHash);
      }
    }
  }, [categories]);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash.startsWith('#faq--') && activeCategory !== 'Todos') {
      requestAnimationFrame(() => {
        document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' });
      });
    } else if (hash === '#contactanos-form') {
      requestAnimationFrame(() => {
        document.getElementById('contactanos-form')?.scrollIntoView({ behavior: 'smooth' });
      });
    }
  }, [activeCategory]);

  const filteredFAQs =
    activeCategory === 'Todos'
      ? visibleFaqs
      : visibleFaqs.filter((f) => f.category === activeCategory);

  function slugify(question: string) {
    return question
      .toLowerCase()
      .replace(/[¿?¡!]/g, '')
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .slice(0, 60);
  }

  const prevTestimonial = () =>
    setTestimonialIndex((i) => (i - 2 + testimonials.length) % testimonials.length);
  const nextTestimonial = () => setTestimonialIndex((i) => (i + 2) % testimonials.length);

  const visibleTestimonials = [
    testimonials[testimonialIndex % testimonials.length],
    testimonials[(testimonialIndex + 1) % testimonials.length],
  ];

  return (
    <main className="min-h-screen bg-white">
      {/* ── 1. Mini Hero ─────────────────────────────────────────────────── */}
      <section ref={heroSection.ref} className="bg-[#00A19B] pt-28 pb-20 px-6">
        <div
          className={`max-w-2xl mx-auto text-center transition-all duration-700 ${
            heroSection.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <span className="inline-block bg-white/20 text-white text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-6">
            Centro de Ayuda
          </span>
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
            Bienvenido a la Ayuda
          </h1>
          <p className="text-white/90 text-lg leading-relaxed">
            ¿Tienes dudas? Aquí las resolvemos. Y si no encuentras lo que buscas, conversemos. En
            IMPACTA.red estamos para acompañarte, queremos que tu experiencia sea simple,
            significativa y a tu medida
          </p>
        </div>
      </section>

      {/* ── 2. FAQ Accordion ─────────────────────────────────────────────── */}
      <section ref={faqSection.ref} className="py-20 px-6 bg-white">
        <div
          id="faq"
          className={`max-w-5xl mx-auto transition-all duration-700 ${
            faqSection.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-[#373A36] mb-4">
              Preguntas Frecuentes
            </h2>
            <p className="text-[#6b6e68] text-lg max-w-xl mx-auto">
              Encuentra respuestas rápidas a las consultas más comunes de nuestra comunidad.
            </p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 justify-center mb-10">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => {
                  setActiveCategory(cat);
                  setOpenFAQ(null);
                  if (cat === 'Todos') {
                    window.history.replaceState(
                      null,
                      '',
                      window.location.pathname + window.location.search
                    );
                  } else {
                    const hash = 'faq--' + cat.toLowerCase().replace(/\s+/g, '-');
                    window.history.replaceState(null, '', '#' + hash);
                  }
                }}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#00A19B] focus-visible:outline-offset-2 ${
                  activeCategory === cat
                    ? 'bg-[#00A19B] text-white'
                    : 'bg-[#F5F5F0] text-[#6b6e68] hover:bg-[#e6f7f6] hover:text-[#00A19B]'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* FAQ Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filteredFAQs.map((faq) => {
              const id = slugify(faq.question);
              const isOpen = openFAQ === faq.question;
              return (
                <div
                  key={id}
                  className="border border-[#E8E8E4] rounded-2xl overflow-hidden transition-all duration-200 hover:border-[#00A19B]/40"
                >
                  <button
                    onClick={() => setOpenFAQ(isOpen ? null : faq.question)}
                    className="w-full flex items-center justify-between px-6 py-5 text-left gap-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#00A19B] focus-visible:outline-offset-2 rounded"
                    aria-expanded={isOpen}
                    aria-controls={id}
                  >
                    <span className="font-semibold text-[#373A36] text-sm leading-snug">
                      {faq.question}
                    </span>
                    <span
                      className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-all duration-300 ${
                        isOpen ? 'bg-[#00A19B] text-white rotate-45' : 'bg-[#F5F5F0] text-[#6b6e68]'
                      }`}
                    >
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path
                          d="M6 1v10M1 6h10"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                    </span>
                  </button>
                  {isOpen && (
                    <div id={id} className="px-6 pb-5">
                      <div className="h-px bg-[#E8E8E4] mb-4" />
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          p: ({ children }) => (
                            <p className="text-[#6b6e68] text-sm leading-relaxed mb-3 last:mb-0">
                              {children}
                            </p>
                          ),
                          strong: ({ children }) => (
                            <strong className="font-semibold text-[#373A36]">{children}</strong>
                          ),
                          a: ({ href, children }) => (
                            <a
                              href={href}
                              className="text-[#00A19B] underline hover:text-[#008580] transition-colors"
                            >
                              {children}
                            </a>
                          ),
                          ul: ({ children }) => (
                            <ul className="list-disc pl-5 text-[#6b6e68] text-sm leading-relaxed mb-3 space-y-1">
                              {children}
                            </ul>
                          ),
                          li: ({ children }) => <li>{children}</li>,
                        }}
                      >
                        {faq.answer}
                      </ReactMarkdown>
                      {activeCategory === 'Todos' && (
                        <span className="inline-block mt-3 text-xs font-semibold text-[#00A19B] bg-[#e6f7f6] px-3 py-1 rounded-full">
                          {faq.category}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── 3. Contact Form ──────────────────────────────────────────────── */}
      <section id="contactanos-form" ref={contactSection.ref} className="py-20 px-6 bg-[#F9F9F6]">
        <div
          className={`max-w-2xl mx-auto transition-all duration-700 ${
            contactSection.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="text-center mb-12">
            <h2
              id="contactanos-title"
              className="text-3xl lg:text-4xl font-bold text-[#373A36] mb-4"
            >
              Contáctanos
            </h2>
            <p className="text-[#6b6e68] text-lg">
              ¿No encontraste lo que buscabas? Escríbenos y te responderemos a la brevedad.
            </p>
          </div>

          <ContactForm />
        </div>
      </section>

      {/* ── 4. Testimonials Carousel ─────────────────────────────────────── */}
      {FEATURE_TESTIMONIOS_ENABLED && (
        <section id="testimonios" ref={testimonialsSection.ref} className="py-20 px-6 bg-white">
          <div
            className={`max-w-5xl mx-auto transition-all duration-700 ${
              testimonialsSection.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-[#373A36] mb-4">
                Testimonios de Clientes
              </h2>
              <p className="text-[#6b6e68] text-lg max-w-xl mx-auto">
                Lo que dicen quienes ya han vivido la experiencia IMPACTA.red.
              </p>
            </div>

            <div
              className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8"
              aria-live="polite"
              aria-atomic="false"
            >
              {visibleTestimonials.map((t, idx) => (
                <div
                  key={`${testimonialIndex}-${idx}`}
                  className="bg-[#F9F9F6] rounded-2xl p-8 border border-[#E8E8E4] flex flex-col gap-5"
                >
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} width="16" height="16" viewBox="0 0 24 24" fill="#E0DF00">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                      </svg>
                    ))}
                  </div>
                  <p className="text-[#373A36] text-base leading-relaxed italic flex-1">
                    &ldquo;{t.quote}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <AppImage
                      src={t.avatar}
                      alt={t.alt}
                      width={44}
                      height={44}
                      className="w-11 h-11 rounded-full object-cover"
                    />

                    <div>
                      <p className="font-bold text-sm text-[#373A36]">{t.author}</p>
                      <p className="text-xs text-[#6b6e68]">{t.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-center gap-4">
              <button
                onClick={prevTestimonial}
                className="w-10 h-10 rounded-full border-2 border-[#E8E8E4] flex items-center justify-center text-[#6b6e68] hover:border-[#00A19B] hover:text-[#00A19B] transition-all duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#00A19B] focus-visible:outline-offset-2"
                aria-label="Anterior testimonio"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <div className="flex gap-2">
                {[0, 2].map((i) => (
                  <button
                    key={i}
                    onClick={() => setTestimonialIndex(i)}
                    className={`w-2 h-2 rounded-full transition-all duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#00A19B] focus-visible:outline-offset-2 ${
                      testimonialIndex === i ? 'bg-[#00A19B] w-6' : 'bg-[#E8E8E4]'
                    }`}
                    aria-label={`Ir al testimonio ${i + 1}`}
                  />
                ))}
              </div>
              <button
                onClick={nextTestimonial}
                className="w-10 h-10 rounded-full border-2 border-[#E8E8E4] flex items-center justify-center text-[#6b6e68] hover:border-[#00A19B] hover:text-[#00A19B] transition-all duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#00A19B] focus-visible:outline-offset-2"
                aria-label="Siguiente testimonio"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </div>
          </div>
        </section>
      )}

      {/* ── 5. CTA Section ───────────────────────────────────────────────── */}
      <section id="privacidad" ref={ctaSection.ref} className="relative py-24 px-6 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('/assets/images/ayuda/cta-background.jpg')",
          }}
        />

        <div className="absolute inset-0 bg-[#00A19B]/85" />
        <div
          className={`relative max-w-2xl mx-auto text-center transition-all duration-700 ${
            ctaSection.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">¿Necesitas Más Ayuda?</h2>
          <p className="text-white/90 text-lg mb-10 leading-relaxed">
            Nuestro equipo está disponible para ayudarte de lunes a viernes de 9:00 a 18:00 hrs. No
            dudes en contactarnos, estamos aquí para ti.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#contacto"
              onClick={(e) => {
                e.preventDefault();
                document
                  .querySelector('section:nth-of-type(3)')
                  ?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-bold text-sm bg-[#E0DF00] text-[#373A36] hover:bg-white hover:text-[#00A19B] transition-all duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#00A19B] focus-visible:outline-offset-2"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
              Enviar un mensaje
            </a>
            <Link
              href="/activities"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-bold text-sm border-2 border-white text-white hover:bg-white hover:text-[#00A19B] transition-all duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#00A19B] focus-visible:outline-offset-2"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              Explorar actividades
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
